import { Node, Edge } from 'reactflow';
import type { CardQuestion, SurveyPath, CardSurvey, SurveyQuestionTemplate } from '../types';
import { DONE_QUESTION_ID, DONE_STEP_JSON_ID } from '../types';
import { normalizeTriggerValues } from './pathTriggerValues';

/** Next integer after the highest question number (for appending "done" step). Order-independent. */
function getDoneStepNumber(questions: CardQuestion[]): number {
  if (questions.length === 0) return 1;
  let maxNum = 0;
  for (const q of questions) {
    const n = typeof q.number === 'number' ? q.number : parseFloat(String(q.number));
    if (!Number.isNaN(n) && n > maxNum) maxNum = n;
  }
  return Math.floor(maxNum) + 1;
}

/**
 * Convert React Flow format (nodes/edges) to Card format
 * This enables migration from old surveys to new format
 */
export function flowToCard(nodes: Node[], edges: Edge[]): CardSurvey {
  const questions: CardQuestion[] = [];
  const paths: SurveyPath[] = [];
  const pathMap = new Map<string, SurveyPath>();

  // Filter out start/end nodes (end node becomes the "done" step in card format)
  const questionNodes = nodes.filter(
    (node) => node.id !== 'start' && node.id !== 'end' && node.type === 'question'
  );

  // Process each question node
  questionNodes.forEach((node) => {
    const meta = node.data?.meta as SurveyQuestionTemplate | undefined;
    const isConditionalNode = node.id.includes('.');
    // Prefer response_question for conditional sub-questions (legacy conditional_config shape)
    const rawText =
      (isConditionalNode && (meta as { response_question?: string })?.response_question) ||
      meta?.question ||
      (node.data?.question as string) ||
      parseQuestionText(node.data?.label || '');
    const questionText = normalizeQuestionText(rawText);

    // Determine if this is a conditional sub-question (e.g., "2.1")
    const isConditional = node.id.includes('.');
    const parentId = isConditional ? node.id.split('.')[0] : null;

    // Find conditional edge that leads to this node (old format uses data.conditionalOption, new uses data.isConditional)
    const conditionalEdge = edges.find((edge) => {
      if (edge.target !== node.id) return false;
      return (
        edge.data?.isConditional === true ||
        (edge.data?.conditionalOption != null && edge.data?.conditionalOption !== 'else')
      );
    });

    let pathId: string | undefined;
    if (conditionalEdge && parentId) {
      // Check if path already exists for this parent-option combination
      const option =
        (typeof conditionalEdge.label === 'string' ? conditionalEdge.label : '') ||
        (conditionalEdge.data?.conditionalOption != null ? String(conditionalEdge.data.conditionalOption) : '') ||
        '';
      const existingPath = Array.from(pathMap.values()).find(
        (p) => p.trigger.questionId === parentId && normalizeTriggerValues(p.trigger.values).includes(option)
      );

      if (existingPath) {
        pathId = existingPath.id;
        existingPath.questionIds.push(node.id);
      } else {
        // Create new path
        const newPath: SurveyPath = {
          id: `path-${parentId}-${option}-${Date.now()}`,
          name: `${option} Path`,
          trigger: {
            questionId: parentId,
            values: [option],
            label: option,
          },
          questionIds: [node.id],
          afterPath: 'continue',
        };
        pathMap.set(newPath.id, newPath);
        pathId = newPath.id;
      }
    }

    const question: CardQuestion = {
      id: node.id,
      number: meta?.number || extractNumericId(node.id),
      text: questionText,
      type: meta?.type || 'textfield',
      required: true, // Default to required, can be enhanced later
      config: {
        format: meta?.type_config?.format as 'numerical' | 'satisfaction' | undefined,
        rateAmount: meta?.type_config?.rate_amount,
        options: meta?.type_config?.radio_options || meta?.type_config?.checkbox_options,
      },
      pathId,
    };

    questions.push(question);
  });

  // Convert path map to array
  const pathsArray = Array.from(pathMap.values());

  // Append "done" as last step (reward/completion marker); reader expects id + number + type
  const hasDone = questions.some((q) => q.type === 'done');
  if (!hasDone) {
    const doneNumber = getDoneStepNumber(questions);
    questions.push({
      id: DONE_STEP_JSON_ID,
      number: doneNumber,
      type: 'done',
    } as CardQuestion);
  }

  return {
    format: 'card-v1',
    questions,
    paths: pathsArray,
  };
}

/**
 * Convert Card format to React Flow format (nodes/edges)
 * This enables backward compatibility and preview functionality
 */
export function cardToFlow(cardSurvey: CardSurvey): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Create start node
  nodes.push({
    id: 'start',
    type: 'input',
    position: { x: 250, y: 0 },
    data: { label: 'Start' },
  });

  // Sort questions by number; exclude "done" (end marker) from nodes—we connect last real question to "end"
  const sortedQuestions = [...cardSurvey.questions]
    .filter((q) => q.type !== 'done' && q.id !== DONE_QUESTION_ID)
    .sort((a, b) => {
      const numA = typeof a.number === 'number' ? a.number : parseFloat(a.number.toString());
      const numB = typeof b.number === 'number' ? b.number : parseFloat(b.number.toString());
      return numA - numB;
    });

  // Create question nodes
  const questionNodes = sortedQuestions.map((question, index) => {
    const yPos = (index + 1) * 150;

    const meta: SurveyQuestionTemplate = {
      number: typeof question.number === 'number' ? question.number : parseFloat(question.number.toString()),
      type: question.type,
      question: question.text,
      type_config: {
        format: question.config.format,
        rate_amount: question.config.rateAmount,
        radio_options: question.type === 'radio' ? question.config.options : undefined,
        checkbox_options: question.type === 'checkbox' ? question.config.options : undefined,
      },
    };

    return {
      id: question.id,
      type: 'question',
      position: { x: 250, y: yPos },
      data: {
        label: `Q${question.number}: ${question.text}`,
        question: question.text,
        meta,
      },
    };
  });

  nodes.push(...questionNodes);

  // Create edges - connect questions sequentially
  for (let i = 0; i < questionNodes.length; i++) {
    const source = i === 0 ? 'start' : questionNodes[i - 1].id;
    const target = questionNodes[i].id;

    edges.push({
      id: `edge-${source}-${target}`,
      source,
      target,
      type: 'smoothstep',
    });
  }

  // Add edges for paths (conditional logic)
  cardSurvey.paths.forEach((path) => {
    const triggerQuestion = cardSurvey.questions.find((q) => q.id === path.trigger.questionId);
    if (!triggerQuestion) return;

    // Connect trigger question to first path question
    if (path.questionIds.length > 0) {
      const firstPathQuestion = path.questionIds[0];
      edges.push({
        id: `path-edge-${triggerQuestion.id}-${firstPathQuestion}`,
        source: triggerQuestion.id,
        target: firstPathQuestion,
        type: 'smoothstep',
        label: normalizeTriggerValues(path.trigger.values).join(', '),
        data: { isConditional: true },
      });
    }

    // Connect path questions sequentially
    for (let i = 0; i < path.questionIds.length - 1; i++) {
      edges.push({
        id: `path-edge-${path.questionIds[i]}-${path.questionIds[i + 1]}`,
        source: path.questionIds[i],
        target: path.questionIds[i + 1],
        type: 'smoothstep',
        data: { isConditional: true },
      });
    }

    // Connect last path question to next main question (or to end if only "done" follows)
    if (path.afterPath === 'continue' && path.questionIds.length > 0) {
      const lastPathQuestion = path.questionIds[path.questionIds.length - 1];
      const nextMainQuestion = findNextMainQuestion(
        cardSurvey.questions,
        triggerQuestion.id,
        path.questionIds
      );
      if (nextMainQuestion) {
        edges.push({
          id: `path-edge-${lastPathQuestion}-${nextMainQuestion.id}`,
          source: lastPathQuestion,
          target: nextMainQuestion.id,
          type: 'smoothstep',
        });
      } else {
        edges.push({
          id: `path-edge-${lastPathQuestion}-end`,
          source: lastPathQuestion,
          target: 'end',
          type: 'smoothstep',
        });
      }
    }
  });

  // Create end node (last real question or path connects to end; "done" in card is the marker, not a flow node)
  nodes.push({
    id: 'end',
    type: 'output',
    position: { x: 250, y: (questionNodes.length + 1) * 150 },
    data: { label: 'End' },
  });
  const lastQuestion = questionNodes[questionNodes.length - 1];
  if (lastQuestion) {
    edges.push({
      id: 'edge-end',
      source: lastQuestion.id,
      target: 'end',
      type: 'smoothstep',
    });
  }

  return { nodes, edges };
}

/**
 * Helper: Extract numeric ID from string
 */
function extractNumericId(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Helper: Parse question text from label (strip "Q1:" or "Q2.1:" prefix)
 */
function parseQuestionText(label: string): string {
  const match = label.match(/^Q\d+(?:\.\d+)?:\s*(.+)$/s);
  return match ? match[1].trim() : label;
}

/**
 * Strip duplicated prefix ("Q2.1:") and trailing "Type: ..." so we store only the question sentence.
 */
function normalizeQuestionText(text: string): string {
  let out = (text || '').trim();
  // Remove leading "Q<number>:" or "Q<number>.<sub>:" so we don't duplicate when displaying "2.1. {text}"
  out = out.replace(/^Q\d+(?:\.\d+)?:\s*/i, '').trim();
  // Remove trailing "\nType: <type>" or " Type: <type>" (from legacy formatConditionalLabel)
  out = out.replace(/\s*\n?\s*Type:\s*.+$/i, '').trim();
  return out || text.trim();
}

/**
 * Helper: Find next main question after a path (skips "done" end marker).
 */
function findNextMainQuestion(
  questions: CardQuestion[],
  triggerQuestionId: string,
  pathQuestionIds: string[]
): CardQuestion | null {
  const triggerIndex = questions.findIndex((q) => q.id === triggerQuestionId);
  if (triggerIndex === -1) return null;

  for (let i = triggerIndex + 1; i < questions.length; i++) {
    const q = questions[i];
    if (q.type === 'done' || q.id === DONE_QUESTION_ID) continue;
    if (!pathQuestionIds.includes(q.id) && !q.pathId) return q;
  }

  return null;
}

